import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';

const AutonomousSupportService = () => {
  const navigate = useNavigate();
  const [selectedFeature, setSelectedFeature] = useState('overview');

  const features = [
    {
      id: 'chatbot',
      title: 'Intelligent Chatbots',
      icon: 'smart_toy',
      description: 'Advanced conversational AI that handles customer inquiries with human-like understanding and responses.',
      benefits: ['24/7 availability', 'Multi-language support', 'Context awareness', 'Seamless escalation'],
      metrics: { resolution: '85%', response: '<2 seconds', satisfaction: '4.7/5' }
    },
    {
      id: 'ticketing',
      title: 'Smart Ticket Routing',
      icon: 'route',
      description: 'Automated ticket classification and routing to the most qualified support agents based on expertise and workload.',
      benefits: ['Intelligent prioritization', 'Skill-based routing', 'Load balancing', 'SLA compliance'],
      metrics: { accuracy: '92%', efficiency: '+40%', resolution: '60% faster' }
    },
    {
      id: 'knowledge',
      title: 'Knowledge Base AI',
      icon: 'library_books',
      description: 'Self-updating knowledge base that learns from interactions and provides instant access to relevant information.',
      benefits: ['Auto-updating content', 'Semantic search', 'Personalized results', 'Usage analytics'],
      metrics: { accuracy: '96%', coverage: '10K+ articles', updates: 'Real-time' }
    },
    {
      id: 'analytics',
      title: 'Support Analytics',
      icon: 'analytics',
      description: 'Comprehensive analytics and insights to optimize support operations and improve customer satisfaction.',
      benefits: ['Performance metrics', 'Trend analysis', 'Predictive insights', 'Custom dashboards'],
      metrics: { insights: '50+ metrics', forecasting: '95% accuracy', reporting: 'Real-time' }
    }
  ];

  const capabilities = [
    {
      title: 'Natural Language Processing',
      description: 'Advanced NLP for understanding customer intent and context',
      icon: 'chat',
      color: 'from-green-500 to-teal-500'
    },
    {
      title: 'Sentiment Analysis',
      description: 'Real-time emotion detection to prioritize urgent issues',
      icon: 'sentiment_satisfied',
      color: 'from-blue-500 to-indigo-500'
    },
    {
      title: 'Automated Workflows',
      description: 'Streamlined processes for common support scenarios',
      icon: 'workflow',
      color: 'from-purple-500 to-pink-500'
    },
    {
      title: 'Omnichannel Support',
      description: 'Unified experience across all communication channels',
      icon: 'hub',
      color: 'from-orange-500 to-red-500'
    }
  ];

  const useCases = [
    {
      title: 'E-commerce Support',
      description: 'Handle order inquiries, returns, and product questions with automated precision.',
      icon: 'shopping_cart',
      industries: ['E-commerce', 'Retail', 'Marketplace']
    },
    {
      title: 'SaaS Customer Success',
      description: 'Provide technical support, onboarding assistance, and feature guidance for software products.',
      icon: 'cloud',
      industries: ['SaaS', 'Software', 'Technology']
    },
    {
      title: 'Financial Services',
      description: 'Secure, compliant support for banking, insurance, and financial product inquiries.',
      icon: 'account_balance',
      industries: ['Banking', 'Insurance', 'Fintech']
    },
    {
      title: 'Healthcare Support',
      description: 'HIPAA-compliant patient support, appointment scheduling, and medical information assistance.',
      icon: 'local_hospital',
      industries: ['Healthcare', 'Telemedicine', 'Medical Devices']
    }
  ];

  const channels = [
    { name: 'Live Chat', icon: 'chat', description: 'Website and app chat support' },
    { name: 'Email', icon: 'email', description: 'Automated email responses' },
    { name: 'Phone', icon: 'phone', description: 'Voice AI and call routing' },
    { name: 'Social Media', icon: 'share', description: 'Social platform monitoring' },
    { name: 'WhatsApp', icon: 'message', description: 'WhatsApp Business API' },
    { name: 'Slack', icon: 'forum', description: 'Internal team support' }
  ];

  const integrations = [
    { name: 'Zendesk', icon: 'support_agent', category: 'Help Desk' },
    { name: 'Salesforce', icon: 'business', category: 'CRM' },
    { name: 'Intercom', icon: 'chat', category: 'Messaging' },
    { name: 'Slack', icon: 'forum', category: 'Communication' },
    { name: 'Jira', icon: 'bug_report', category: 'Issue Tracking' },
    { name: 'HubSpot', icon: 'hub', category: 'Marketing' }
  ];

  return (
    <div className="min-h-screen bg-dark">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 via-transparent to-teal-500/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500/20 to-teal-500/20 rounded-2xl flex items-center justify-center">
                <span className="material-symbols-outlined text-green-400 text-3xl">smart_toy</span>
              </div>
              <div className="text-left">
                <h1 className="text-4xl md:text-6xl font-bold text-white">
                  Autonomous <span className="bg-gradient-to-r from-green-400 to-teal-400 bg-clip-text text-transparent">Support</span>
                </h1>
                <p className="text-gray-400 text-lg mt-2">AI-Powered Customer Service Platform</p>
              </div>
            </div>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Transform your customer support with intelligent automation that provides instant, accurate, 
              and personalized assistance across all channels while reducing costs and improving satisfaction.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all transform hover:scale-105">
                Try Live Demo
              </button>
              <button className="border border-gray-600 hover:border-green-500 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all">
                View Case Studies
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Grid */}
      <section className="py-16 bg-gray-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">AI-Powered Capabilities</h2>
            <p className="text-gray-300 text-lg">Advanced features that revolutionize customer support</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {capabilities.map((capability, index) => (
              <div key={index} className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-green-500/50 transition-all group">
                <div className={`w-12 h-12 bg-gradient-to-r ${capability.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <span className="material-symbols-outlined text-white text-2xl">{capability.icon}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{capability.title}</h3>
                <p className="text-gray-300 text-sm">{capability.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Communication Channels */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Omnichannel Support</h2>
            <p className="text-gray-300 text-lg">Provide consistent support across all customer touchpoints</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {channels.map((channel, index) => (
              <div key={index} className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-green-500/50 transition-all">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <span className="material-symbols-outlined text-green-400">{channel.icon}</span>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{channel.name}</h3>
                    <p className="text-gray-400 text-sm">{channel.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Navigation */}
      <section className="py-12 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {features.map((feature) => (
              <button
                key={feature.id}
                onClick={() => setSelectedFeature(feature.id)}
                className={`flex items-center gap-3 px-6 py-3 rounded-lg transition-all ${
                  selectedFeature === feature.id
                    ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white shadow-lg'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <span className="material-symbols-outlined">{feature.icon}</span>
                <span className="font-medium">{feature.title}</span>
              </button>
            ))}
          </div>

          {/* Feature Details */}
          {features.map((feature) => (
            selectedFeature === feature.id && (
              <div key={feature.id} className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 bg-gradient-to-r from-green-500/20 to-teal-500/20 rounded-lg flex items-center justify-center">
                        <span className="material-symbols-outlined text-green-400 text-2xl">{feature.icon}</span>
                      </div>
                      <h3 className="text-2xl font-bold text-white">{feature.title}</h3>
                    </div>
                    <p className="text-gray-300 text-lg mb-6">{feature.description}</p>
                    <div className="space-y-3">
                      <h4 className="text-white font-semibold">Key Features:</h4>
                      <ul className="space-y-2">
                        {feature.benefits.map((benefit, index) => (
                          <li key={index} className="flex items-center gap-3 text-gray-300">
                            <span className="w-2 h-2 bg-gradient-to-r from-green-400 to-teal-400 rounded-full"></span>
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="bg-gray-900 rounded-xl p-6">
                    <h4 className="text-white font-semibold mb-4">Performance Metrics</h4>
                    <div className="grid grid-cols-1 gap-4">
                      {Object.entries(feature.metrics).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center p-3 bg-gray-800 rounded-lg">
                          <span className="text-gray-400 capitalize">{key}</span>
                          <span className="text-green-400 font-bold">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )
          ))}
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Industry Solutions</h2>
            <p className="text-gray-300 text-lg">Tailored support automation for every industry</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-green-500/50 transition-all">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-green-500/20 to-teal-500/20 rounded-lg flex items-center justify-center">
                    <span className="material-symbols-outlined text-green-400 text-2xl">{useCase.icon}</span>
                  </div>
                  <h3 className="text-xl font-bold text-white">{useCase.title}</h3>
                </div>
                <p className="text-gray-300 mb-4">{useCase.description}</p>
                <div className="flex flex-wrap gap-2">
                  {useCase.industries.map((industry, idx) => (
                    <span key={idx} className="bg-gradient-to-r from-green-500/10 to-teal-500/10 text-green-400 px-3 py-1 rounded-full text-sm border border-green-500/20">
                      {industry}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="py-16 bg-gray-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Platform Integrations</h2>
            <p className="text-gray-300 text-lg">Connect with your existing support and business tools</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {integrations.map((integration, index) => (
              <div key={index} className="bg-gray-800 rounded-lg p-4 text-center hover:bg-gray-700 transition-all group">
                <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:bg-gradient-to-r group-hover:from-green-500/20 group-hover:to-teal-500/20 transition-all">
                  <span className="material-symbols-outlined text-green-400">{integration.icon}</span>
                </div>
                <h4 className="text-white font-medium text-sm mb-1">{integration.name}</h4>
                <p className="text-gray-400 text-xs">{integration.category}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-500/10 to-teal-500/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Revolutionize Your Customer Support</h2>
          <p className="text-xl text-gray-300 mb-8">
            Join thousands of companies already using AI to provide exceptional customer experiences while reducing support costs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/signup')}
              className="bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all transform hover:scale-105"
            >
              Start Free Trial
            </button>
            <button 
              onClick={() => navigate('/contact')}
              className="border border-green-500 hover:bg-gradient-to-r hover:from-green-500 hover:to-teal-500 text-green-400 hover:text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all"
            >
              Schedule Demo
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AutonomousSupportService;